import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { DEFAULT_WRITER_SYSTEM_PROMPT } from './prompts'

export type TabKey = 'search' | 'outline' | 'write' | 'final'

export type UIState = {
  activeTab: TabKey
  theme: string
  searchText: string
  outlinePlan: string
  systemPrompt: string
  articleText: string
  finalText: string
  isStreaming: boolean
  streamedCount: number
  progressLog: string[]
  autoAdvance: boolean
  autoCopyToFinal: boolean
  authToken: string | null
  isAuthed: boolean

  // Actions
  setActiveTab: (t: TabKey) => void
  setTheme: (v: string) => void
  setSearchText: (v: string) => void
  setOutlinePlan: (v: string) => void
  setSystemPrompt: (v: string) => void
  setArticleText: (v: string) => void
  setFinalText: (v: string) => void
  appendLog: (line: string) => void
  clearLog: () => void
  setIsStreaming: (v: boolean) => void
  setStreamedCount: (n: number) => void
  setAutoAdvance: (v: boolean) => void
  setAutoCopyToFinal: (v: boolean) => void
  setAuthToken: (v: string | null) => void
  setIsAuthed: (v: boolean) => void
  logout: () => void
  resetAll: () => void
}

const initial: Partial<UIState> = {
  activeTab: 'search',
  systemPrompt: DEFAULT_WRITER_SYSTEM_PROMPT,
  autoAdvance: true,
  autoCopyToFinal: true,
}

export const useUI = create<UIState>()(
  persist(
    (set, get) => ({
      activeTab: initial.activeTab as TabKey,
      theme: '',
      searchText: '',
      outlinePlan: '',
      systemPrompt: initial.systemPrompt!,
      articleText: '',
      finalText: '',
      isStreaming: false,
      streamedCount: 0,
      progressLog: [],
      autoAdvance: initial.autoAdvance!,
      autoCopyToFinal: initial.autoCopyToFinal!,
      authToken: null,
      isAuthed: false,

      setActiveTab: (t) => set({ activeTab: t }),
      setTheme: (v) => set({ theme: v }),
      setSearchText: (v) => set({ searchText: v }),
      setOutlinePlan: (v) => set({ outlinePlan: v }),
      setSystemPrompt: (v) => set({ systemPrompt: v }),
      setArticleText: (v) => set({ articleText: v }),
      setFinalText: (v) => set({ finalText: v }),
      appendLog: (line) => set((s) => ({ progressLog: [...s.progressLog, line] })),
      clearLog: () => set({ progressLog: [] }),
      setIsStreaming: (v) => set({ isStreaming: v }),
      setStreamedCount: (n) => set({ streamedCount: n }),
      setAutoAdvance: (v) => set({ autoAdvance: v }),
      setAutoCopyToFinal: (v) => set({ autoCopyToFinal: v }),
      setAuthToken: (v) => set({ authToken: v }),
      setIsAuthed: (v) => set({ isAuthed: v }),
      logout: () => {
        set({ authToken: null, isAuthed: false })
      },
      resetAll: () => {
        // Clear persisted UI storage and reset in-memory state to defaults (but keep auth)
        try { localStorage.removeItem('egshv3-ui-v1') } catch {}
        set({
          activeTab: 'search',
          theme: '',
          searchText: '',
          outlinePlan: '',
          systemPrompt: DEFAULT_WRITER_SYSTEM_PROMPT,
          articleText: '',
          finalText: '',
          isStreaming: false,
          streamedCount: 0,
          progressLog: [],
          autoAdvance: true,
          autoCopyToFinal: true,
        })
      },
    }),
    {
      name: 'egshv3-ui-v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeTab: state.activeTab,
        theme: state.theme,
        searchText: state.searchText,
        outlinePlan: state.outlinePlan,
        systemPrompt: state.systemPrompt,
        articleText: state.articleText,
        finalText: state.finalText,
        isStreaming: state.isStreaming,
        streamedCount: state.streamedCount,
        progressLog: state.progressLog,
        autoAdvance: state.autoAdvance,
        autoCopyToFinal: state.autoCopyToFinal,
        authToken: state.authToken,
        isAuthed: state.isAuthed,
      }),
      merge: (persisted: any, current: any) => {
        const merged = { ...current, ...persisted }
        // Safety: never rehydrate into a streaming state
        return { ...merged, isStreaming: false }
      },
      onRehydrateStorage: () => (state) => {
        // ensure flags are sane after rehydrate
        if (state) {
          state.isStreaming = false
        }
      },
    }
  )
)
